import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { PASSES_API_BASE } from '../api/config';
import { getToken } from '../api/tokenStore';
import { PASS_STATUS, listOpenPasses, getPass } from '../api/studentPassesApi';
import { refreshTokenFromSession } from '../context/AuthContext';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

// The pass shape delivered by both the REST endpoints (listOpenPasses/getPass)
// and the Socket.io pass:* events — the generated PassResponse schema. Its
// status enum is now the canonical PassStatus (PENDING/WAITING/ACTIVE/
// COMPLETED/CANCELLED/DENIED/EXPIRED), so no widening is needed; the terminal-
// status guard compares directly against PASS_STATUS.COMPLETED. Shared with
// useSchoolPasses and useStudentPassEvents, which reuse this hook's return value.
export type Pass = components['schemas']['PassResponse'];

// The runtime pass statuses, matching the generated schema exactly (kept as a
// named export for consumers that reference the status union directly).
export type PassStatus = Pass['status'];

// A live pass event: the subscribed socket event name plus its pass payload.
export interface PassEvent {
  type: string;
  pass: Pass;
}

// The connection lifecycle states surfaced to consumers.
export type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'unauthenticated';

// Pass state keyed by pass id, as accumulated from socket events + resyncs.
export type PassesById = Record<number, Pass>;

// Public return shape of usePassSocket, consumed by useSchoolPasses and
// useStudentPassEvents.
export interface UsePassSocketResult {
  passes: Pass[];
  passesById: PassesById;
  connectionStatus: ConnectionStatus;
  lastEvent: PassEvent | null;
  resync: () => Promise<void>;
}

const TERMINAL_STATUSES = new Set<string>([
  PASS_STATUS.COMPLETED,
  PASS_STATUS.CANCELLED,
  PASS_STATUS.DENIED,
  PASS_STATUS.EXPIRED,
]);

// Backoff/cap for the 'Unauthorized' connect_error retry loop below: if
// refreshTokenFromSession() keeps returning a token that still doesn't
// authenticate against the socket server (e.g. backend session revoked but
// the refresh endpoint hasn't caught up), this prevents an unbounded rapid
// reconnect loop from hammering the auth service and the passes-socket
// service.
const MAX_RECONNECT_ATTEMPTS = 6;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

// Shared exponential-backoff formula for both the Unauthorized token-refresh
// retry loop and the plain network-drop retry loop below. Jittered to
// [50%, 100%] of the computed delay so that many clients dropped by the same
// event (e.g. every socket on a school's live board during a passes-api
// redeploy) don't all retry in lockstep and hammer the server at the same
// instant. Always <= the un-jittered value, so a caller advancing fake
// timers by the full un-jittered delay is guaranteed the timeout has fired.
function nextReconnectDelay(attempt: number): number {
  const delay = Math.min(
    BASE_RECONNECT_DELAY_MS * 2 ** attempt,
    MAX_RECONNECT_DELAY_MS
  );
  return delay / 2 + Math.random() * (delay / 2);
}

// Every real pass state transition sets one of these to a new value (see
// packages/db prisma schema's Pass model + passes-api's create/approve/
// deny/cancel/return/expire routes); requestedAt is always present, so
// this always yields a comparable 'last touched' timestamp even though
// there is no single generic updatedAt/version field on the pass.
const STATUS_TIMESTAMP_FIELDS = [
  'requestedAt',
  'approvedAt',
  'activatedAt',
  'returnedAt',
  'deniedAt',
  'cancelledAt',
  'expiredAt',
] as const;

function latestTimestamp(pass: Pass): number {
  let latest = 0;
  for (const field of STATUS_TIMESTAMP_FIELDS) {
    const value = pass[field];
    if (!value) continue;
    const ms = new Date(value).getTime();
    if (!Number.isNaN(ms) && ms > latest) latest = ms;
  }
  return latest;
}

// True when `existing` reflects a strictly later lifecycle event than
// `incoming` and should therefore be kept instead of overwritten. Ties
// (equal or unparseable/missing timestamps on both sides) fall back to
// accepting `incoming`, matching the previous always-overwrite behavior
// for same-age data.
function isStale(existing: Pass | undefined, incoming: Pass): boolean {
  return !!existing && latestTimestamp(existing) > latestTimestamp(incoming);
}

// Newest-first ordering by requestedAt, shared by every consumer that sorts
// pass lists (useSchoolPasses.js's `passes`, useStudentPassEvents.js's
// `passes` and `recentPasses`) so the comparator exists in exactly one place.
export function byRequestedAtDesc(a: Pass, b: Pass): number {
  return new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime();
}

function assertReturnedStatusIsCompleted(pass: Pass, logLabel: string): void {
  if (pass.status !== PASS_STATUS.COMPLETED) {
    logError(
      new Error(
        `pass:returned delivered status "${pass.status}", expected "${PASS_STATUS.COMPLETED}"`
      ),
      'client_socket',
      `${logLabel}:pass:returned`
    );
  }
}

/**
 * Shared Socket.io connect/reconnect/resync/terminal-status-guard boilerplate
 * for the student ('user:{id}' room) and staff ('school:{schoolId}' room)
 * pass-events hooks. The only difference between the two consumers is the
 * subscribed event-name list (staff also gets 'pass:requested').
 *
 * @param eventNames - socket event names to subscribe pass upserts to
 * @param logLabel - used in logError context strings, kept consumer-specific
 *   so log output still distinguishes the two call sites
 */
export function usePassSocket(
  eventNames: string[],
  logLabel: string
): UsePassSocketResult {
  const [passesById, setPassesById] = useState<PassesById>({});
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('connecting');
  const [lastEvent, setLastEvent] = useState<PassEvent | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const passesByIdRef = useRef<PassesById>({});

  useEffect(() => {
    passesByIdRef.current = passesById;
  }, [passesById]);

  const upsertPass = useCallback((pass: Pass) => {
    setPassesById((prev) => {
      if (isStale(prev[pass.id], pass)) {
        return prev;
      }
      return { ...prev, [pass.id]: pass };
    });
  }, []);

  const resync = useCallback(async (): Promise<void> => {
    try {
      const { data } = await listOpenPasses();
      const openIds = new Set(data.map((pass) => pass.id));
      const prev = passesByIdRef.current;

      // Previously-known passes that are neither terminal nor still open may
      // have transitioned to a terminal state while the socket was
      // disconnected (a missed event) — refetch them individually instead of
      // silently dropping them.
      const staleIds = Object.values(prev)
        .filter(
          (pass) => !TERMINAL_STATUSES.has(pass.status) && !openIds.has(pass.id)
        )
        .map((pass) => pass.id);

      const refetched = await Promise.all(
        staleIds.map((id) =>
          getPass(id).catch((error: unknown) => {
            logError(error, 'client_socket', `${logLabel}:resync:refetch`);
            return null;
          })
        )
      );

      setPassesById((current) => {
        const next = { ...current };
        data.forEach((pass) => {
          if (!isStale(next[pass.id], pass)) {
            next[pass.id] = pass;
          }
        });
        refetched.forEach((pass) => {
          if (pass && !isStale(next[pass.id], pass)) {
            next[pass.id] = pass;
          }
        });
        return next;
      });
    } catch (error) {
      logError(error, 'client_socket', `${logLabel}:resync`);
    }
  }, [logLabel]);

  const passes = useMemo(
    () => Object.values(passesById).sort(byRequestedAtDesc),
    [passesById]
  );

  useEffect(() => {
    let cancelled = false;
    const socket = io(PASSES_API_BASE, {
      auth: (cb: (data: object) => void) => cb({ token: getToken() }),
      // Built-in reconnection is disabled in favor of the manual retry loops
      // below: the Unauthorized branch needs to refresh the token before
      // reconnecting (something socket.io's own reconnection can't do), and
      // reusing the same manual approach for plain network drops keeps both
      // loops in one place instead of racing socket.io's automatic retries
      // against ours.
      reconnection: false,
    });
    socketRef.current = socket;
    let reconnectAttempts = 0;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    // Separate counter/timeout for non-auth disconnects and connect_errors
    // (network blips, backend redeploys, laptop sleep) so a burst of those
    // can't eat into the Unauthorized loop's MAX_RECONNECT_ATTEMPTS budget.
    // Unlike the Unauthorized loop, this one never gives up - a transient
    // drop must not permanently kill the connection.
    let networkRetryAttempts = 0;
    let networkRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNetworkRetry = (): void => {
      const delay = nextReconnectDelay(networkRetryAttempts);
      networkRetryAttempts += 1;
      networkRetryTimeoutId = setTimeout(() => {
        if (!cancelled) socket.connect();
      }, delay);
    };

    const handleConnect = (): void => {
      reconnectAttempts = 0;
      networkRetryAttempts = 0;
      setConnectionStatus('connected');
      resync();
    };
    const handleDisconnect = (reason: string): void => {
      setConnectionStatus('disconnected');
      // 'io client disconnect' only ever comes from our own explicit
      // socket.disconnect() calls below (the unauthenticated paths) - don't
      // schedule a retry for those. Every other reason is a drop worth
      // retrying, including 'io server disconnect' (the server side closing
      // the connection, e.g. during a passes-api redeploy) - deliberately,
      // since socket.io's own built-in reconnection excludes exactly that
      // reason from auto-retry, and a redeploy is one of the scenarios this
      // fix targets - as well as 'ping timeout' / 'transport close' /
      // 'transport error' (network blips, laptop sleep).
      if (reason === 'io client disconnect') return;
      scheduleNetworkRetry();
    };

    const handleConnectError = async (err: Error): Promise<void> => {
      if (err?.message !== 'Unauthorized') {
        setConnectionStatus('disconnected');
        scheduleNetworkRetry();
        return;
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        setConnectionStatus('unauthenticated');
        socket.disconnect();
        return;
      }
      reconnectAttempts += 1;
      try {
        const session = await refreshTokenFromSession();
        if (cancelled) return;
        if (session?.session?.token) {
          const delay = nextReconnectDelay(reconnectAttempts - 1);
          retryTimeoutId = setTimeout(() => {
            if (!cancelled) socket.connect();
          }, delay);
        } else {
          setConnectionStatus('unauthenticated');
          socket.disconnect();
        }
      } catch (error) {
        if (cancelled) return;
        logError(error, 'client_socket', `${logLabel}:token-refresh`);
        setConnectionStatus('unauthenticated');
        socket.disconnect();
      }
    };

    const handlers = eventNames.map(
      (eventName: string): [string, (pass: Pass) => void] => {
        const handler = (pass: Pass): void => {
          if (eventName === 'pass:returned')
            assertReturnedStatusIsCompleted(pass, logLabel);
          upsertPass(pass);
          setLastEvent({ type: eventName, pass });
        };
        return [eventName, handler];
      }
    );

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    handlers.forEach(([eventName, handler]) => socket.on(eventName, handler));

    return () => {
      cancelled = true;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      if (networkRetryTimeoutId) clearTimeout(networkRetryTimeoutId);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      handlers.forEach(([eventName, handler]) =>
        socket.off(eventName, handler)
      );
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { passes, passesById, connectionStatus, lastEvent, resync };
}

export default usePassSocket;
